// annotation_frontend\src\components\admin\AdminValidationViewer.js
import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Select, MenuItem, FormControl, InputLabel, Grid, Button, CircularProgress, Alert, Paper, List, ListItem, ListItemText, ListItemIcon, Divider, IconButton, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckIcon from '@mui/icons-material/Check';
import ImageCanvas from '../annotation/ImageCanvas'; // Reuse the canvas
import { getImages, getImageDetails, getAllSetsForImage, getAnnotations, validateAnnotationSet } from '../../services/apiService';
import { toast } from 'react-toastify';
import LoadingSpinner from '../common/LoadingSpinner';

const AdminValidationViewer = () => {
    const [imageList, setImageList] = useState([]);
    const [selectedImageId, setSelectedImageId] = useState('');
    const [selectedImageDetails, setSelectedImageDetails] = useState(null);
    const [annotationSets, setAnnotationSets] = useState([]); // Metadata of sets for selected image
    const [annotationsToDisplay, setAnnotationsToDisplay] = useState([]); // Actual annotations of the viewed set
    const [viewedSetInfo, setViewedSetInfo] = useState(null); // Info of the set being viewed
    const [imageSize, setImageSize] = useState([0, 0]);
    const [isLoadingImages, setIsLoadingImages] = useState(false);
    const [isLoadingSets, setIsLoadingSets] = useState(false);
    const [isLoadingAnnotations, setIsLoadingAnnotations] = useState(false);
    const [isLoadingImage, setIsLoadingImage] = useState(false);
    const [isApplyingValidation, setIsApplyingValidation] = useState(false);
    const [error, setError] = useState('');
    const [imageLoadError, setImageLoadError] = useState('');

    // Fetch image list on mount
    useEffect(() => {
        const fetchImageList = async () => {
            setIsLoadingImages(true);
            setError('');
            try {
                const response = await getImages();
                setImageList(response.data || []);
            } catch (err) {
                console.error("Error fetching image list:", err);
                setError('Failed to load image list.');
                toast.error('Failed to load image list.');
            } finally {
                setIsLoadingImages(false);
            }
        };
        fetchImageList();
    }, []);

    // Fetch sets when image selection changes
    const handleImageSelection = useCallback(async (imageId) => {
        setSelectedImageId(imageId);
        // Reset states
        setSelectedImageDetails(null);
        setAnnotationSets([]);
        setAnnotationsToDisplay([]);
        setViewedSetInfo(null);
        setImageSize([0, 0]);
        setError('');
        setImageLoadError('');

        if (!imageId) return;

        setIsLoadingSets(true);
        setIsLoadingImage(true); // Also indicate image loading starts

        try {
            // Fetch image details first
            const detailsResponse = await getImageDetails(imageId);
            if (detailsResponse.data?.fullUrl) {
                setSelectedImageDetails(detailsResponse.data);
                if (detailsResponse.data.size) setImageSize(detailsResponse.data.size);
            } else {
                throw new Error(`Failed to load image details for ID ${imageId}`);
            }

            // Fetch all set metadata for this image
            const setsResponse = await getAllSetsForImage(imageId);
            setAnnotationSets(setsResponse.data || []);

            // Automatically view the 'validated' or 'original' set initially? Optional.
            // Example: Find validated or original and load its annotations
            const initialSet = setsResponse.data.find(s => s.is_validated) || setsResponse.data.find(s => s.is_original);
            if (initialSet) {
                await handleViewAnnotations(initialSet.set_id);
            }

        } catch (err) {
            console.error("Error loading image or sets for validation:", err);
            const errMsg = err.response?.data?.error || err.message || `Failed to load data for image ID ${imageId}.`;
            setError(errMsg);
            setImageLoadError(errMsg); // Set image specific error
            toast.error(errMsg);
        } finally {
            setIsLoadingSets(false);
            // setIsLoadingImage will be handled by effect below
        }
    }, []); // Removed dependency on handleViewAnnotations

    // Effect to manage the visual image loading state
    useEffect(() => {
        if ((selectedImageDetails?.fullUrl && imageSize[0] === 0) || isLoadingAnnotations || isLoadingSets) {
            setIsLoadingImage(true);
        }
        if ((imageSize[0] > 0 && imageSize[1] > 0) || imageLoadError) {
            setIsLoadingImage(false);
        }
        if (!selectedImageDetails) {
            setIsLoadingImage(false);
        }
    }, [selectedImageDetails, imageSize, isLoadingAnnotations, isLoadingSets, imageLoadError]);


    // Fetch and display annotations for a specific set ID
    const handleViewAnnotations = async (setId) => {
        if (!setId) return;
        setIsLoadingAnnotations(true);
        setAnnotationsToDisplay([]); // Clear previous
        setViewedSetInfo(null);
        setError(''); // Clear general errors

        const params = { setId: setId }; // Fetch by specific set ID
        try {
            const response = await getAnnotations(params);
            const loadedAnnotations = (response.data.annotations || []).map((ann) => ({
                ...ann,
                id: ann.annotation_id,
                annotationType: ann.annotation_type,
            }));
            setAnnotationsToDisplay(loadedAnnotations);
            setViewedSetInfo(response.data.set_info); // Store info of the set being viewed
        } catch (err) {
            console.error(`Error fetching annotations for set ${setId}:`, err);
            const errMsg = err.response?.data?.error || `Failed to load annotations for set ${setId}.`;
            setError(errMsg); // Show error related to annotation loading
            toast.error(errMsg);
        } finally {
            setIsLoadingAnnotations(false);
        }
    };

    // Handle marking a set as validated
    const handleValidate = async (setIdToValidate) => {
        if (!setIdToValidate || isApplyingValidation) return;
        setIsApplyingValidation(true);
        setError('');
        try {
            await validateAnnotationSet(setIdToValidate);
            toast.success(`Annotation Set ID ${setIdToValidate} marked as validated!`);
            // Refresh the list of sets to show the updated status
            if (selectedImageId) {
                const setsResponse = await getAllSetsForImage(selectedImageId);
                setAnnotationSets(setsResponse.data || []);
                // Optionally, keep viewing the just-validated set
                //setViewedSetInfo(setsResponse.data.find(s => s.set_id === setIdToValidate));
            }
        } catch (err) {
            console.error(`Error validating set ${setIdToValidate}:`, err);
            const errMsg = err.response?.data?.error || `Failed to validate set ${setIdToValidate}.`;
            setError(errMsg);
            toast.error(errMsg);
        } finally {
            setIsApplyingValidation(false);
        }
    };

    return (
        <Box>
            <Typography variant="h5" gutterBottom>Annotation Validation</Typography>

            {/* Image Selector */}
            <FormControl fullWidth sx={{ mb: 2 }} disabled={isLoadingImages || isLoadingSets || isApplyingValidation}>
                <InputLabel id="admin-image-select-label">Select Image to Validate</InputLabel>
                <Select
                    labelId="admin-image-select-label"
                    value={selectedImageId}
                    label="Select Image to Validate"
                    onChange={(e) => handleImageSelection(e.target.value)}
                >
                    <MenuItem value=""><em>-- Select Image --</em></MenuItem>
                    {isLoadingImages ? (
                        <MenuItem value="" disabled><CircularProgress size={20} sx={{ mr: 1 }} /> Loading images...</MenuItem>
                    ) : (
                        imageList.map((img) => (
                            <MenuItem key={img.image_id} value={img.image_id}>
                                {img.original_filename || `Image ID: ${img.image_id}`}
                            </MenuItem>
                        ))
                    )}
                    {!isLoadingImages && imageList.length === 0 && <MenuItem value="" disabled>No images found.</MenuItem>}
                </Select>
            </FormControl>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {selectedImageId && (
                <Grid container spacing={2}>
                    {/* Left Side: Image Canvas */}
                    <Grid item xs={12} md={8} lg={9}>
                        <Paper sx={{ p: 2, position: 'relative', minHeight: 400 }}>
                            {selectedImageDetails?.original_filename && (
                                <Typography variant="subtitle1" gutterBottom>
                                    Image: {selectedImageDetails.original_filename}
                                    {viewedSetInfo && (
                                        <Typography variant="caption" sx={{ ml: 1 }}>
                                            (Viewing Set ID: {viewedSetInfo.set_id} - {viewedSetInfo.is_original ? 'Original' : `Submitted by ${annotationSets.find(s => s.set_id === viewedSetInfo.set_id)?.username || 'Unknown'}`}{viewedSetInfo.is_validated ? ', Validated' : ''})
                                        </Typography>
                                    )}
                                </Typography>
                            )}

                            {(isLoadingImage || isLoadingSets) && !imageLoadError && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                                    <LoadingSpinner />
                                    <Typography sx={{ ml: 1 }}>
                                        {isLoadingSets ? 'Loading Annotation Sets...' : 'Loading Image...'}
                                    </Typography>
                                </Box>
                            )}

                            {/* Render canvas only when image details are ready and not loading sets */}
                            {!isLoadingSets && selectedImageDetails?.fullUrl && !imageLoadError && (
                                <>
                                    {isLoadingAnnotations && (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(255,255,255,0.7)', zIndex: 1 }}>
                                            <LoadingSpinner />
                                            <Typography sx={{ ml: 1 }}>Loading Annotations...</Typography>
                                        </Box>
                                    )}
                                    <ImageCanvas
                                        // Use image URL and viewedSet ID as key to force re-render on change
                                        key={`${selectedImageDetails.fullUrl}-${viewedSetInfo?.set_id || 'no-set'}`}
                                        imageUrl={selectedImageDetails.fullUrl}
                                        annotations={annotationsToDisplay}
                                        setAnnotations={() => { }} // Read-only canvas for admin
                                        selectedId={null} // No selection needed
                                        setSelectedId={() => { }}
                                        imageSize={imageSize}
                                        setImageSize={setImageSize}
                                        isLoadingImage={isLoadingImage} // Pass down derived loading state
                                        imageLoadError={imageLoadError}
                                    />
                                </>
                            )}

                            {/* Display Image Load Error */}
                            {imageLoadError && !isLoadingSets && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                                    <Alert severity="error">{imageLoadError}</Alert>
                                </Box>
                            )}

                        </Paper>
                    </Grid>

                    {/* Right Side: Set List & Actions */}
                    <Grid item xs={12} md={4} lg={3}>
                        <Paper sx={{ p: 2, height: '100%' }}>
                            <Typography variant="h6" gutterBottom>Annotation Sets</Typography>
                            {isLoadingSets && <LoadingSpinner />}
                            {!isLoadingSets && annotationSets.length === 0 && <Typography>No annotation sets found for this image.</Typography>}
                            {!isLoadingSets && annotationSets.length > 0 && (
                                <List dense>
                                    {annotationSets.map((set) => (
                                        <React.Fragment key={set.set_id}>
                                            <ListItem
                                                secondaryAction={
                                                    <>
                                                        <Tooltip title="View Annotations">
                                                            <IconButton edge="end" aria-label="view" onClick={() => handleViewAnnotations(set.set_id)} disabled={isLoadingAnnotations}>
                                                                <VisibilityIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        {!set.is_validated && (
                                                            <Tooltip title="Mark as Validated">
                                                                <span> {/* Span needed for disabled button tooltip */}
                                                                    <IconButton
                                                                        edge="end"
                                                                        aria-label="validate"
                                                                        onClick={() => handleValidate(set.set_id)}
                                                                        disabled={isApplyingValidation || isLoadingAnnotations}
                                                                        color="primary"
                                                                    >
                                                                        {isApplyingValidation && viewedSetInfo?.set_id === set.set_id ? <CircularProgress size={20} /> : <CheckIcon />}
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        )}
                                                    </>
                                                }
                                                sx={{ bgcolor: viewedSetInfo?.set_id === set.set_id ? 'action.hover' : 'inherit' }}
                                            >
                                                <ListItemIcon sx={{ minWidth: 30 }}>
                                                    {set.is_validated ? <CheckCircleIcon color="success" fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" color="disabled" />}
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={set.is_original ? 'Original Set' : `User: ${set.username || 'N/A'}`}
                                                    secondary={`ID: ${set.set_id} | Submitted: ${new Date(set.submission_timestamp).toLocaleString()}`}
                                                    primaryTypographyProps={{ variant: 'body2' }}
                                                    secondaryTypographyProps={{ variant: 'caption' }}
                                                />
                                            </ListItem>
                                            <Divider component="li" />
                                        </React.Fragment>
                                    ))}
                                </List>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            )}

        </Box>
    );
};

export default AdminValidationViewer;